import React, { forwardRef } from "react";
import { toWords } from 'number-to-words';

const InvoicePDF = forwardRef(({ bookingData }, ref) => {
    const { stay, rooms, summary, user, guests, booking_code } = bookingData;
    const primaryGuest = guests?.find((g) => g.is_primary_guest) || guests?.[0] || user || {};
    const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;

    // Derive the displayed GST rate from the amount actually charged (real
    // 3-tier gst_rules: 0% / 5% / 18%) instead of a hardcoded "9%" that was
    // wrong for any room outside the top slab.
    const roomBaseForGst = parseFloat(summary?.base_price || 0);
    const effectiveGstPercent = roomBaseForGst > 0
        ? (parseFloat(summary?.room_gst_amount || 0) / roomBaseForGst) * 100
        : 0;
    const isGstExempt = effectiveGstPercent < 0.01;
    const halfGstRate = Math.round((effectiveGstPercent / 2) * 100) / 100;

    return (
        <div
            ref={ref}
            style={{
                padding: 30,
                fontFamily: "Helvetica, Arial, sans-serif",
                width: 800,
                background: "#fff",
                color: "#333",
                border: "1px solid #ddd",
            }}
        >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                    <h1 style={{ margin: 0, color: "#2c3e50" }}>{stay?.title}</h1>
                    <p style={{ margin: "2px 0" }}>{stay?.address}</p>
                    <p style={{ margin: "2px 0" }}>
                        {stay?.gstin ? `GSTIN: ${stay.gstin} | ` : ""}SAC: 996311
                    </p>
                </div>
                <div style={{ textAlign: "right" }}>
                    <h3 style={{ margin: 0, color: "#2c3e50" }}>Invoice</h3>
                    <p style={{ margin: "2px 0" }}>Invoice No: INV/{new Date().getFullYear()}/001</p>
                    <p style={{ margin: "2px 0" }}>Date: {new Date().toLocaleDateString("en-GB")}</p>
                    <p style={{ margin: "2px 0" }}>Place of Supply: West Bengal</p>
                </div>
            </div>

            <hr style={{ borderColor: "#bbb", margin: "15px 0" }} />

            {/* Guest & Booking Info */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                    <h4 style={{ marginBottom: 5 }}>Guest Details</h4>
                    <p style={{ margin: "2px 0" }}><strong>Name:</strong> {primaryGuest.first_name} {primaryGuest.last_name}</p>
                    <p style={{ margin: "2px 0" }}><strong>Address:</strong> {primaryGuest.address || "-"}</p>
                    <p style={{ margin: "2px 0" }}><strong>Booking ID:</strong> {booking_code}</p>
                </div>
                <div>
                    <h4 style={{ marginBottom: 5 }}>Booking Details</h4>
                    <p style={{ margin: "2px 0" }}><strong>Check-in:</strong> {new Date(summary?.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })}</p>
                    <p style={{ margin: "2px 0" }}><strong>Check-out:</strong> {new Date(summary?.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })}</p>
                    <p style={{ margin: "2px 0" }}>
                        <strong>Rooms:</strong>{" "}
                        {rooms?.map((r, i) => (
                            <span key={i}>
                                {r.title} (₹{r.price_per_night}/night × {r.room_count})
                                {i !== rooms.length - 1 ? ", " : ""}
                            </span>
                        ))}
                    </p>
                </div>
            </div>

            {/* Price Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
                <thead>
                    <tr style={{ background: "#2c3e50", color: "#fff" }}>
                        <th style={{ textAlign: "left", padding: 8 }}>Sr. No.</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Description</th>
                        <th style={{ textAlign: "right", padding: 8 }}>No. of Nights</th>
                        <th style={{ textAlign: "right", padding: 8 }}>Rate/Night</th>
                        <th style={{ textAlign: "right", padding: 8 }}>Amount (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {rooms?.map((room, index) => {
                        const roomTotal =
                            (parseFloat(room.price_per_night || 0) *
                                parseFloat(summary?.nights || 1) *
                                parseFloat(room.room_count || 1));

                        return (
                            <tr key={index} style={{ background: index % 2 === 0 ? "#ecf0f1" : "#fff" }}>
                                <td style={{ padding: 8 }}>{index + 1}</td>
                                <td style={{ padding: 8 }}>
                                    Room Charges ({room.title})
                                </td>
                                <td style={{ textAlign: "right", padding: 8 }}>
                                    {summary?.nights} × {room.room_count}
                                </td>
                                <td style={{ textAlign: "right", padding: 8 }}>
                                    {formatCurrency(room.price_per_night)}
                                </td>
                                <td style={{ textAlign: "right", padding: 8 }}>
                                    {formatCurrency(roomTotal)}
                                </td>
                            </tr>
                        );
                    })}
                    {isGstExempt ? (
                        <tr>
                            <td style={{ padding: 8 }}>2</td>
                            <td style={{ padding: 8 }}>GST @ 0% on Accommodation (Nil – Fully Exempt)</td>
                            <td style={{ textAlign: "center", padding: 8 }}>—</td>
                            <td style={{ textAlign: "center", padding: 8 }}>—</td>
                            <td style={{ textAlign: "right", padding: 8 }}>{formatCurrency(0)}</td>
                        </tr>
                    ) : (
                        <>
                            <tr>
                                <td style={{ padding: 8 }}>2</td>
                                <td style={{ padding: 8 }}>CGST @ {halfGstRate}% on Accommodation Services</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "right", padding: 8 }}>{formatCurrency(summary?.room_gst_amount / 2)}</td>
                            </tr>
                            <tr style={{ background: "#ecf0f1" }}>
                                <td style={{ padding: 8 }}>3</td>
                                <td style={{ padding: 8 }}>SGST @ {halfGstRate}% on Accommodation Services</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "right", padding: 8 }}>{formatCurrency(summary?.room_gst_amount / 2)}</td>
                            </tr>
                        </>
                    )}
                    {parseFloat(summary?.commission_amount || 0) > 0 && (
                        <>
                            <tr>
                                <td style={{ padding: 8 }}>4</td>
                                <td style={{ padding: 8 }}>Platform Service Fee ({summary?.commission_percentage}%)</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "right", padding: 8 }}>{formatCurrency(summary?.commission_amount)}</td>
                            </tr>
                            <tr style={{ background: "#ecf0f1" }}>
                                <td style={{ padding: 8 }}>5</td>
                                <td style={{ padding: 8 }}>GST @ 18% on Platform Fee (9% CGST + 9% SGST)</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "center", padding: 8 }}>—</td>
                                <td style={{ textAlign: "right", padding: 8 }}>{formatCurrency(summary?.commission_gst_amount)}</td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>

            {/* Grand Total */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    background: "#2c3e50",
                    color: "#fff",
                    padding: 10,
                    fontSize: 18,
                    fontWeight: "bold",
                }}
            >
                Total: {formatCurrency(summary?.total_price)}
            </div>

            <div style={{ marginTop: 15 }}>
                <p><strong>Grand Total:</strong> {formatCurrency(summary?.total_price)}</p>
                <p><strong>In Words:</strong> {toWords(summary.total_price)} only</p>
            </div>

            <div style={{ marginTop: 20 }}>
                <p><strong>Payment:</strong> Paid via UPI/Card</p>
                <p><em>Note: Hotel GST @18% (with ITC available to hotel). Platform fee + 18% GST collected separately.</em></p>
            </div>

            <div style={{ marginTop: 30, paddingTop: 10, borderTop: "0.5px solid #999", textAlign: "center", fontSize: 9 }}>
                <p style={{ margin: "2px 0", fontWeight: "bold" }}>GUESTLINKUP.COM PRIVATE LIMITED</p>
                <p style={{ margin: "2px 0" }}>Floor No.: GROUND FLOOR &nbsp;|&nbsp; Building No./Flat No.: HOLDING NO. 391/2797</p>
                <p style={{ margin: "2px 0" }}>Name Of Premises/Building: 2ND MILE, SILIGURI &nbsp;|&nbsp; Road/Street: Sevok Road</p>
                <p style={{ margin: "2px 0" }}>Locality/Sub Locality: Jyoti Nagar &nbsp;|&nbsp; City/Town/Village: Siliguri, Jalpaiguri, West Bengal - 734001</p>
                <p style={{ margin: "2px 0", fontWeight: "bold" }}>GSTIN: 19AANCG0675R1ZO</p>
            </div>
        </div>
    );
});

export default InvoicePDF;
